import {
  addDays,
  format,
  startOfDay,
  startOfWeek,
  set,
  isPast,
  addMonths,
  formatDistance,
  parseISO,
  differenceInSeconds,
} from "date-fns";
import { es } from "date-fns/locale";
import { DateTime } from "luxon";
import { TIME_ZONES } from "./TimeInputUtils";
import { DAYS } from "../constants/Days";

export const DAYS_OPTIONS = [
  { id: "Mo", name: "Lunes" },
  { id: "Tu", name: "Martes" },
  { id: "We", name: "Miércoles" },
  { id: "Th", name: "Jueves" },
  { id: "Fr", name: "Viernes" },
  { id: "Sa", name: "Sábado" },
  { id: "Su", name: "Domingo" },
];

export const DATE_TIME_LOCALE = "es-AR";

export const DATE_TIME_FIELD_OPTIONS = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

export const getMonthName = (date) => {
  const name = format(date, "MMMM", { locale: es });
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const shortWeekdayToLong = (day) => {
  switch (day.toUpperCase()) {
    case "MO":
      return "Lunes";
    case "TU":
      return "Martes";
    case "WE":
      return "Miércoles";
    case "TH":
      return "Jueves";
    case "FR":
      return "Viernes";
    case "SA":
      return "Sábado";
    case "SU":
      return "Domingo";
    default:
      return "";
  }
};

const getAllDays = () => {
  const dayValues = Object.values(DAYS);
  const firstDOW = startOfWeek(new Date());
  const shortWeekDaysArray = Array.from(Array(dayValues.length)).map((e, i) =>
    format(addDays(firstDOW, i), "EEEEEE")
  );
  return shortWeekDaysArray;
};

export const getFridayToSaturday = () => {
  return getAllDays().filter((d) => [DAYS.FRIDAY, DAYS.SATURDAY].includes(d));
};

export const getSundayToThursday = () => {
  return getAllDays().filter((d) =>
    [
      DAYS.SUNDAY,
      DAYS.MONDAY,
      DAYS.TUESDAY,
      DAYS.WEDNESDAY,
      DAYS.THURSDAY,
    ].includes(d)
  );
};

export const getTimeFromDate = (date, country) => {
  const timeZone = TIME_ZONES[country];
  const zonedDate = DateTime.fromJSDate(new Date(date), {
    zone: "utc",
  }).setZone(timeZone);
  return zonedDate.toFormat("HH:mm");
};

export const formatDateToLocalISOString = (date, country) => {
  const time = getTimeFromDate(date, country);
  return `${new Date().toISOString().split("T")[0]}T${time}:00Z`;
};

export const setTimeToDate = (time) => {
  const [hours, minutes, seconds] = time.split(":");
  const date = set(new Date(), {
    hours: hours,
    minutes: minutes,
    seconds: seconds || "00",
  });
  return date;
};

export const getNextDateFromDay = (day) => {
  const currentDate = set(new Date(), {
    date: day,
  });
  let nextDate = currentDate;
  if (isPast(currentDate)) {
    nextDate = addMonths(currentDate, 1);
  }

  return format(nextDate, "dd/MM/yyyy");
};

export const formatDateFromISODate = (date) => {
  if (!date) return "";
  return format(new Date(date), "dd/MM/yyyy HH:mm");
};

export const getDaysAgo = (dateString, prefix = "Hace") => {
  if (!dateString) return "-";
  const date = parseISO(dateString);
  const daysAgo = formatDistance(new Date(), date, {
    addSuffix: false,
    locale: es,
  });

  return `${prefix} ${daysAgo}`;
};

export const toDateOnly = (date) => startOfDay(new Date(date));

export const dateHasExpired = (date) => {
  return differenceInSeconds(new Date(date), new Date()) <= 0;
};

export const formatDateRangeWithTime = (startDate, endDate) => {
  const parsedStartDate = parseISO(startDate);
  const parsedEndDate = parseISO(endDate);
  const startHour = format(parsedStartDate, "HH:mm");
  const endHour = format(parsedEndDate, "HH:mm");

  return `${startHour}hs a ${endHour}hs`;
};

/**
 * A stock day is a calendar date, not an instant: the API stores it at UTC
 * midnight and uses it as the lookup key for the day's stock row.
 *
 * DateInput's default format goes through the browser timezone, so 2026-08-11
 * at 00:00 UTC reads back as "2026-08-10" in AR (UTC-3). That is not only a
 * wrong label: saving writes the shifted value back, and since the API replaces
 * the whole day list, the original day's row — with its sold units — is dropped.
 * These two keep the round-trip in UTC.
 */
export const formatUtcDateInput = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

export const parseUtcDateInput = (value) =>
  value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null;

/**
 * Today's date as the same "YYYY-MM-DD" key formatUtcDateInput produces.
 *
 * Read from the local calendar, not from UTC: a stock day labelled 2026-08-11
 * is that calendar day for whoever reads the screen, and after 21:00 in AR the
 * UTC date is already the next one.
 */
export const todayDateKey = () => format(new Date(), "yyyy-MM-dd");

/**
 * Every calendar date between two bounds, inclusive, at UTC midnight.
 *
 * Iterating in UTC on purpose: date-fns' eachDayOfInterval walks the local
 * timezone and would drift off the day key the API uses. Returns [] for an
 * inverted or incomplete range.
 */
export const eachUtcDayInRange = (from, to) => {
  if (!from || !to) return [];

  const start = new Date(parseUtcDateInput(formatUtcDateInput(from)));
  const end = new Date(parseUtcDateInput(formatUtcDateInput(to)));

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end < start) return [];

  const dates = [];
  for (let day = start; day <= end; day = addDays(day, 1)) {
    dates.push(day);
  }

  return dates;
};

// TIME INPUT FUNCTIONS
export * from "./TimeInputUtils";
